import { Box } from "@mui/material";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { UserData } from "../../../../dummy-data/user-data";
import Text from "../../../../components/Text";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useState } from "react";
import UserProfileDialog from "../../../../components/app/settings/user-settings/UserProfileDrawer";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DEFAULT_VALUE_LABEL_LIST,
  DGT_Pagination,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { useUsersList } from "../../../../queries/use-get-users";
import { formatDateTime } from "../../../../utils/date-time-utility";
import { useUpdateProviderUserStatus } from "../../../../queries/use-update-provider-status";
import { useStatusUsers } from "../../../../queries/use-status-users";
import { UsersFormValuesType } from "../../../../constants/usersFormValues";
import { ROLE_RECORD } from "../../../../constants/master";
import ActionMenu from "../../../../components/app/ActionMenu";
import UpsertStaffUserDrawer from "../../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import { useUpdateUser } from "../../../../queries/use-update-user";

interface AdminUserTableProps {
  searchKey: string;
  statusFilter: any;
}

const AdminUserTable = ({ searchKey, statusFilter }: AdminUserTableProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: usersList,
    isFetching,
    refetch,
    isLoading,
  } = useUsersList({
    pagination: pagination,
    searchKey,
    isArchieve: false,
    statusKey:
      statusFilter === "all"
        ? undefined
        : typeof statusFilter === "boolean"
          ? statusFilter
          : undefined,
  });
  // const { mutateAsync: archiveAPI } = useUserArchive();
  const { mutateAsync: providerStatusUpdate } = useUpdateProviderUserStatus();
  const { mutateAsync: statusChange } = useStatusUsers();

  function toggleStatus<T>(value: boolean, id: T, role: string) {
    if (role === "PROVIDER") {
      return providerStatusUpdate({
        providerId: id?.toString() ?? "",
        status: value,
      }).then(() => refetch());
    }

    return statusChange({
      userId: id?.toString() ?? "",
      status: value,
    }).then(() => refetch());
  }
  const COLUMNS = applyDefaultColumnSettings<UserData>([
    {
      field: "uuid",
      flex: 0.5,
      // minWidth: 40,
      headerName: "User ID",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
        );
      },
    },
    {
      field: "userInfo",
      flex: 0.7,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "User Name",
      renderCell: ({ row }) => (
        <Text
          onClick={() => {
            // temp
            const updatedValues = {
              /**
               * assigning both as same name
               * @field  B.E change first_name and last_name
               * @todo remove this once done
               *  */
              firstName: row?.firstName ?? "",
              lastName: row.lastName ?? "",
              email: row?.email ?? "",
              phone: row?.phone ?? "",
              role: row?.role ?? "",
              provider_group_location: row?.location ?? "",
              active: row?.active,
            } as UsersFormValuesType;
            setSelectedUserData(updatedValues);
            userProfileModalDisclosure.open();
          }}
          marginBottom={"3px"}
          color={"primary.main"}
          fontWeight={"medium"}
          sx={{ cursor: "pointer" }}
        >
          {row?.firstName ?? "" + " " + row?.lastName ?? ""}
        </Text>
      ),
      sortable: false,
    },
    {
      field: "role",
      flex: 0.7,
      minWidth: 130,
      disableColumnMenu: true,
      headerName: "Role",
      sortable: false,
      renderCell: ({ ...params }) => {
        return <Text>{params.value ? ROLE_RECORD[params.value] : "-"}</Text>;
      },
    },
    {
      field: "phone",
      flex: 0.6,
      minWidth: 140,
      disableColumnMenu: true,
      headerName: "Contact",
      sortable: false,
    },
    {
      field: "email",
      flex: 0.7,
      minWidth: 200,
      disableColumnMenu: true,
      headerName: "Email",
      renderCell: ({ row, value }) => (
        <Flex alignItems={"center"} gap={"4px"}>
          <VerifiedIcon
            color={row.emailVerified ? "success" : "disabled"}
            fontSize="small"
          />
          <Text>{value}</Text>
        </Flex>
      ),
      sortable: false,
    },
    {
      field: "lastLogin",
      flex: 0.7,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Last Sign In",
      sortable: false,
      renderCell: ({ ...params }) => {
        return <Text>{params.value ? formatDateTime(params.value) : "-"}</Text>;
      },
    },
    {
      field: "active",
      disableColumnMenu: true,
      headerName: "Status",
      width: 150,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row.uuid.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id, row?.roleType)}
        />
      ),
      sortable: false,
    },
    {
      field: "Action",
      width: 90,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              { value: "edit", label: "Edit" },
              // { value: "delete", label: "Delete", isDisabled: row?.active },
            ]}
            onActionClick={(v) => {
              if (v === "edit") handleOpenEditProfileModal(row);
            }}
          />
        );
      },
    },
  ]);

  const [userProfileModalDisclosure, editProfileDisClosure] =
    useReduceDisclosure(2);
  const { mutateAsync: updateStaffUser } = useUpdateUser();
  const [selectedUserData, setSelectedUserData] = useState<any>();

  function handleOpenEditProfileModal(user: UserData) {
    setSelectedUserData(user);
    editProfileDisClosure.open();
  }
  const editStaffUser = (values: any) => {
    const transFormData: any = getUnNecessaryTransformData(
      ["workLocations"],
      values
    );

    return updateStaffUser({
      user: transFormData,
    }).then((res) => {
      // refetch();
      return res;
    });
  };

  // const {
  //   opened: openedDeleteModal,
  //   open: openDeleteModal,
  //   close: closeDeleteModal,
  //   deleteId,
  // } = useDeleteDisClosure(false);

  return (
    <Box height={"100%"} width={"100%"}>
      <CustomDataGridV2
        loading={isLoading || isFetching}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        searchString={searchKey}
        getRowId={(row) => row.uuid}
        rows={usersList?.data?.content ?? []}
        rowCount={usersList?.data?.page?.totalElements}
        columns={COLUMNS}
        disableRowSelectionOnClick
      />

      <UserProfileDialog
        disClosureHost={userProfileModalDisclosure}
        userId={selectedUserData?.uuid}
        dialogTitle="User Profile"
      />

      {/* <DeleteModal
        w="20vw"
        open={openedDeleteModal}
        onClose={closeDeleteModal}
        id={deleteId}
        onConfirmation={(id) =>
          archiveAPI({
            userId: id as string,
            status: true,
            // xTENANTID: params.providerGroupId,
          }).then((res) => {
            refetch();
            return res;
          })
        }
        content={"You want to delete this User"}
      /> */}

      <UpsertStaffUserDrawer
        open={editProfileDisClosure.opened}
        modalTitle="Edit User"
        onClose={editProfileDisClosure.close}
        initialValues={{
          ...(selectedUserData as any),
          workLocations: (selectedUserData as any)?.workLocations
            ? transformMultiSelectFrontEndVal(
                DEFAULT_VALUE_LABEL_LIST,
                (selectedUserData as any)?.workLocations
              )
            : undefined,
        }}
        onSubmit={editStaffUser}
      />
    </Box>
  );
};

export default AdminUserTable;
